// @flow
import type { Node } from 'react';
import React from 'react';
import styled, { type StyledComponent } from 'styled-components';

import { Edit, TrashSmall } from '@rover/icons';
import { DSTokenMap, MQ } from '@rover/kibble/styles';

import BigRadioButton, { type Props as RadioButtonProps } from '../../buttons/BigRadioButton';
import Button, { ButtonType } from '../../buttons/Button';

const V_CENTER = `
  display: flex;
  align-items: center;
`;

const Wrapper: StyledComponent<{}> = styled.label`
  margin-bottom: 0;
  ${V_CENTER} width: 100%;
  flex-direction: row;
  border: 1px solid ${DSTokenMap.BORDER_COLOR_PRIMARY};
  padding: 15px;
  cursor: pointer;
  font-weight: normal;

  &:first-child {
    border-top-left-radius: ${DSTokenMap.BORDER_RADIUS_SECONDARY};
    border-top-right-radius: ${DSTokenMap.BORDER_RADIUS_SECONDARY};
  }

  &:last-child {
    border-bottom-left-radius: ${DSTokenMap.BORDER_RADIUS_SECONDARY};
    border-bottom-right-radius: ${DSTokenMap.BORDER_RADIUS_SECONDARY};
  }

  &:not(:first-child) {
    border-top: none;
  }

  ${MQ.XS} {
    align-items: flex-start;
  }
`;

const RadioButtonWrapper: StyledComponent<{}> = styled.div`
  width: 28px;
  margin-right: 5px;

  ${MQ.XS} {
    margin-top: 3px;
  }
`;

export const ContentWrapper: StyledComponent<{}> = styled.div`
  ${V_CENTER} flex-direction: row;
  justify-content: space-between;
  width: 100%;
  padding-right: 30px;

  ${MQ.XS} {
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
  }
`;

const DeleteButtonWrapper: StyledComponent<{}> = styled.div`
  ${MQ.XS} {
    margin-right: 0;
  }
`;

export const DeleteButton: StyledComponent<{}> = styled(Button)`
  position: relative;
  padding: 0;
  width: 40px;
  height: 40px;
`;

const EditButtonWrapper: StyledComponent<{}> = styled.div`
  ${MQ.XS} {
    margin-right: 0;
  }
`;

export const EditButton: StyledComponent<{}> = styled(Button)`
  position: relative;
  width: 40px;
  height: 40px;
  padding: 0;
`;

export const EmptyButtonSpacer: StyledComponent<{}> = styled.div`
  position: relative;
  width: 40px;
  height: 40px;
  min-width: 40px;
  min-height: 40px;
  padding 0;
  margin-right: 0;
`;

const handleClicked = (event, cb) => {
  event.preventDefault();
  cb();
};

const renderDelete = (value, onDelete) => {
  if (!onDelete) return null;

  return (
    <DeleteButtonWrapper>
      <DeleteButton
        role="button"
        buttonType={ButtonType.TEXT}
        data-qa-id="components-ff-radio-rows-buttons-delete"
        onClick={(e) => handleClicked(e, () => onDelete(value))}
      >
        <TrashSmall
          margin={DSTokenMap.SPACE_2X}
          height={DSTokenMap.SPACE_6X}
          width={DSTokenMap.SPACE_6X}
          fill={DSTokenMap.TEXT_COLOR_SECONDARY}
        />
      </DeleteButton>
    </DeleteButtonWrapper>
  );
};

const renderEdit = (value, onEdit) => {
  if (!onEdit) return null;

  return (
    <EditButtonWrapper>
      <EditButton
        role="button"
        buttonType={ButtonType.TEXT}
        data-qa-id="components-ff-radio-rows-buttons-edit"
        onClick={(e) => handleClicked(e, () => onEdit(value))}
      >
        <Edit
          margin={DSTokenMap.SPACE_2X}
          height={DSTokenMap.SPACE_6X}
          width={DSTokenMap.SPACE_6X}
          fill={DSTokenMap.TEXT_COLOR_SECONDARY}
        />
      </EditButton>
    </EditButtonWrapper>
  );
};

export type Props = {
  options: Array<{
    value: string,
    label?: string,
    editValue: any,
    children: Node,
    disableDelete?: boolean,
    isUpdatableElement?: boolean,
  }>,
  name: string,
  radioProps: $Shape<RadioButtonProps>,
  value: string,
  isUpdatable: boolean,
  onChange: (string) => void,
  onDelete: ?(string) => void,
  onEdit: (any) => void,
};

const shouldRenderEditButtonForElement = (
  isGloballyUpdatable: boolean,
  isUpdatableElement: ?boolean
): ?boolean => {
  return isUpdatableElement === undefined ? isGloballyUpdatable : isUpdatableElement;
};

const RadioRows = ({
  options,
  value,
  name,
  isUpdatable,
  onChange,
  onDelete,
  onEdit,
  radioProps,
  ...other
}: Props) => (
  <div>
    {options.map((option) => (
      <Wrapper key={option.value} {...other}>
        <RadioButtonWrapper>
          <BigRadioButton
            name={name}
            value={option.value}
            checked={option.value === value}
            onChange={() => onChange(option.value)}
            {...radioProps}
          />
        </RadioButtonWrapper>
        <ContentWrapper>{option.label || option.children}</ContentWrapper>
        {shouldRenderEditButtonForElement(isUpdatable, option.isUpdatableElement)
          ? renderEdit(option.editValue, onEdit)
          : null}
        {!option.disableDelete ? renderDelete(option.value, onDelete) : <EmptyButtonSpacer />}
      </Wrapper>
    ))}
  </div>
);

RadioRows.defaultProps = {
  value: '',
  onDelete: undefined,
  onEdit: undefined,
  radioProps: {},
};

export default RadioRows;



// WEBPACK FOOTER //
// ../react-lib/src/components/formFields/RadioRows/RadioRows.jsx